import { z } from 'zod';
import type { RoleName } from '@/features/auth/types/auth.types';

export interface CompanyUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phone?: string | undefined;
  roles: RoleName[];
  isActive: boolean;
  lastLoginAt?: string | undefined;
  createdAt: string;
}

export interface CompanyStats {
  totalUsers: number;
  activeUsers: number;
  pendingVerifications: number;
}

// Roles that can be assigned by company admin (excludes SUPER_ADMIN)
export type CompanyRoleName = 'ADMIN' | 'MANAGER' | 'AGENT';

// Request DTO for creating a company user
export interface CreateCompanyUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  phone?: string | undefined;
  initialRole: CompanyRoleName;
  sendPasswordResetEmail?: boolean | undefined;
}

// Request DTO for updating a company user
export interface UpdateCompanyUserRequest {
  firstName: string;
  lastName: string;
  phone?: string | undefined;
}

// Zod schema for creating a company user
export const createUserSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  phone: z.string().optional(),
  initialRole: z.enum(['ADMIN', 'MANAGER', 'AGENT'] as const),
});

export type CreateUserFormData = z.infer<typeof createUserSchema>;

// Data for editing a user (profile + role change)
export interface EditUserData {
  firstName: string;
  lastName: string;
  phone?: string | undefined;
  role: CompanyRoleName;
}

// Role display labels
export const ROLE_LABELS: Record<CompanyRoleName, string> = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  AGENT: 'Agent',
};
